const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Qaraqalpaq Market';

const TOKEN_KEY = 'qm_admin_token';

/**
 * Токен администратора хранится в sessionStorage (живёт до закрытия вкладки).
 * Защита от XSS обеспечивается строгой CSP (infra/nginx) и отсутствием dangerouslySetInnerHTML.
 */
export const tokenStore = {
  get: () => {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t: string | null) => {
    try {
      if (t) sessionStorage.setItem(TOKEN_KEY, t);
      else sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* приватный режим */
    }
  },
};

export class AdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

let onUnauthorized: () => void = () => undefined;
export const setOnUnauthorized = (fn: () => void) => {
  onUnauthorized = fn;
};

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const token = tokenStore.get();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...init, headers, body });
  } catch {
    throw new AdminApiError(0, 'NETWORK_ERROR', 'Нет соединения с сервером');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null;
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/admin/auth/login')) onUnauthorized();
    throw new AdminApiError(res.status, data?.error?.code ?? 'INTERNAL_ERROR', data?.error?.message ?? `HTTP ${res.status}`, data?.error?.details);
  }
  return data as T;
}

export const qs = (p: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};

const ERROR_TEXT: Record<string, string> = {
  NETWORK_ERROR: 'Нет соединения с сервером',
  UNAUTHORIZED: 'Требуется вход',
  FORBIDDEN: 'Недостаточно прав',
  NOT_FOUND: 'Не найдено',
  CONFLICT: 'Данные изменились — обновите страницу',
  INVALID_STATUS_TRANSITION: 'Действие недоступно в текущем статусе',
  VALIDATION_FAILED: 'Проверьте поля формы',
  RATE_LIMITED: 'Слишком много попыток, подождите',
  FEATURE_DISABLED: 'Функция выключена в настройках',
  IMAGE_INVALID: 'Файл не является допустимым изображением',
  TWO_FACTOR_REQUIRED: 'Введите код из приложения-аутентификатора или резервный код',
  TWO_FACTOR_SETUP_REQUIRED: 'Сначала подключите двухфакторный вход (раздел «Безопасность»)',
};

export function errorText(e: unknown): string {
  if (e instanceof AdminApiError) {
    const fields = (e.details as { fields?: Record<string, string> } | undefined)?.fields;
    const base = ERROR_TEXT[e.code] ?? e.message;
    return fields ? `${base}: ${Object.keys(fields).join(', ')}` : base;
  }
  return 'Неизвестная ошибка';
}

export interface Paged<T> {
  total: number;
  page?: number;
  pageSize?: number;
  items: T[];
}

export interface AdminMe {
  id: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'moderator' | 'ads_manager';
  permissions: string[];
  twoFactor: { enabled: boolean; required: boolean };
}
